import React, { useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import noop from 'lodash/noop';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import BoardWithContext, { normalizeColumns } from './BoardWithContext';
import { ViewMode, ViewField } from './enum';
import DataSet from '../data-set';
import { FieldType } from '../data-set/enum';
export const VIEWLISTDS = '__VIEWLISTDS__';
export const DEFAULTVIEW = {
    card: {
        [ViewField.viewName]: '初始卡片视图',
        [ViewField.viewMode]: ViewMode.card,
        [ViewField.id]: '__DEFAULT__',
    },
    kanban: {
        [ViewField.viewName]: '初始看板视图',
        [ViewField.viewMode]: ViewMode.kanban,
        [ViewField.id]: '__DEFAULT__',
    },
    table: {
        [ViewField.viewName]: '初始列表视图',
        [ViewField.viewMode]: ViewMode.table,
        [ViewField.id]: '__DEFAULT__',
    },
};
const Board = function Board(props) {
    const { getConfig, getCustomizable } = useContext(ConfigContext);
    const { defaultViewMode, defaultViewProps, tableProps, viewVisible, dataSet, customizedCode, customizable = customizedCode ? getCustomizable('Board') : undefined } = props;
    const $customizable = customizedCode ? customizable : false;
    const [loaded, setLoaded] = useState(!$customizable);
    const [customized, setCustomized] = useState();
    const customizedDS = useMemo(() => new DataSet({
        autoLocateFirst: false,
        paging: false,
        fields: [
            {
                name: ViewField.viewProps,
                type: FieldType.json,
            },
            {
                name: ViewField.viewName,
                type: FieldType.string,
            },
            {
                name: ViewField.viewMode,
                type: FieldType.string,
                transformResponse(value) {
                    return value.split('_VIEW')[0].toLocaleLowerCase();
                },
                transformRequest(value) {
                    return value.concat('_VIEW').toLocaleUpperCase();
                },
            },
            {
                name: ViewField.id,
            },
            {
                name: ViewField.activeKey,
            },
            {
                name: ViewField.groupField,
                bind: `${ViewField.viewProps}.${ViewField.groupField}`,
            },
            {
                name: ViewField.viewHeight,
                bind: `${ViewField.viewProps}.${ViewField.viewHeight}`,
            },
            {
                name: ViewField.cardWidth,
                bind: `${ViewField.viewProps}.${ViewField.cardWidth}`,
            },
            {
                name: ViewField.showLabel,
                bind: `${ViewField.viewProps}.${ViewField.showLabel}`,
            },
            {
                name: ViewField.displayFields,
                bind: `${ViewField.viewProps}.${ViewField.displayFields}`,
            },
            {
                name: ViewField.combineSort,
                bind: `${ViewField.viewProps}.${ViewField.combineSort}`,
            },
            {
                name: ViewField.sort,
                bind: `${ViewField.viewProps}.${ViewField.sort}`,
            },
        ],
        events: {
            load: ({ dataSet }) => {
                const activeRecord = dataSet.find(record => record.get(ViewField.activeKey));
                if (activeRecord && viewVisible) {
                    const viewType = activeRecord.get(ViewField.viewMode);
                    if (viewVisible[viewType] !== false) {
                        dataSet.current = dataSet.find(record => record.get(ViewField.activeKey));
                    }
                    else {
                        dataSet.current = dataSet.find(record => record.get(ViewField.id) === '__DEFAULT__');
                    }
                }
                else {
                    dataSet.current = dataSet.find(record => record.get(ViewField.id) === '__DEFAULT__');
                }
            },
        },
    }), [customizedCode]);
    const displayFields = useMemo(() => {
        if (tableProps && tableProps.columns) {
            return tableProps.columns;
        }
        if (tableProps && tableProps.children) {
            const { children, aggregation } = tableProps;
            const generatedColumns = normalizeColumns(children, aggregation);
            return generatedColumns[0].concat(generatedColumns[1], generatedColumns[2]);
        }
        return [];
    }, [tableProps]);
    /**
     * 加载当前默认个性化视图数据(单个)
     */
    const loadCustomized = useCallback(async () => {
        if (customizedCode) {
            setLoaded(false);
            const customizedLoad = getConfig('customizedLoad');
            try {
                const res = await customizedLoad(customizedCode, 'Board', {
                    type: 'default',
                });
                const remoteCustomized = res ? [res] : [];
                const viewProps = {
                    card: {
                        [ViewField.cardWidth]: 6,
                        [ViewField.displayFields]: displayFields.map(field => field.name).filter(Boolean).slice(0, 3),
                        [ViewField.showLabel]: 1,
                    },
                    table: {},
                    kanban: {
                        [ViewField.cardWidth]: 6,
                        [ViewField.displayFields]: displayFields.map(field => field.name).filter(Boolean).slice(0, 3),
                        [ViewField.showLabel]: 1,
                    },
                };
                const defaultView = {
                    code: customizedCode,
                    ...DEFAULTVIEW[defaultViewMode],
                    [ViewField.viewProps]: {
                        ...viewProps[defaultViewMode],
                        ...defaultViewProps[defaultViewMode],
                    },
                };
                if (remoteCustomized && remoteCustomized.length) {
                    remoteCustomized.push(defaultView);
                    customizedDS.loadData(remoteCustomized);
                }
                else {
                    customizedDS.loadData([defaultView]);
                }
                dataSet.setState(VIEWLISTDS, customizedDS);
            }
            finally {
                setLoaded(true);
            }
        }
    }, [customizedCode]);
    useEffect(() => {
        if ($customizable) {
            loadCustomized();
        }
    }, [$customizable, loadCustomized]);
    return loaded ? (React.createElement(BoardWithContext, Object.assign({}, props, { customizedDS: customizedDS, customized: customized, customizedCode: customizedCode, setCustomized: setCustomized, customizable: $customizable }))) : null;
};
Board.displayName = 'Board';
Board.defaultProps = {
    onChange: noop,
    viewMode: ViewMode.table,
    queryFields: {},
    autoQuery: false,
    viewVisible: true,
    commandsLimit: 1,
    defaultViewMode: ViewMode.table,
    defaultViewProps: {},
};
export default Board;
